import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import essayService from '../services/essayService.js';
import InlineMarkupEditor from '../components/InlineMarkupEditor.js';
import '../styles/AIAnalysisPanel.css';

const EssayResultPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [essay, setEssay] = useState(null);
  const [error, setError] = useState('');
  const [showMarkups, setShowMarkups] = useState(true);
  const [activeRange, setActiveRange] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await essayService.getEssayById(id);
        if (!mounted) return;
        setEssay(data || null);
      } catch (err) {
        console.error(err);
        setError('Não foi possível carregar a redação.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  const analysis = essay?.analysis || {};
  const rawAnalysis = useMemo(() => analysis?.raw || {}, [analysis]);
  const inlineErrors = useMemo(() => analysis.inlineErrors || [], [analysis]);
  const feedbackSummary = useMemo(() => {
    const summary = typeof analysis.feedbackSummary === 'string' ? analysis.feedbackSummary.trim() : '';
    if (summary) return summary;
    return typeof analysis.comments === 'string' ? analysis.comments.trim() : '';
  }, [analysis]);
  useEffect(() => {
    if (!essay?.id) return;
    const safeAnalysis = analysis && typeof analysis === 'object' ? analysis : {};
    const raw = safeAnalysis.raw && typeof safeAnalysis.raw === 'object' ? safeAnalysis.raw : null;
    const meta = {
      hasRaw: !!raw,
      strengths: Array.isArray(safeAnalysis.strengths) ? safeAnalysis.strengths.length : 0,
      rawStrengths: Array.isArray(raw?.strengths) ? raw.strengths.length : null,
      repertorio: Array.isArray(safeAnalysis.repertorioSociocultural) ? safeAnalysis.repertorioSociocultural.length : 0,
      rawRepertorio: Array.isArray(raw?.repertorioSociocultural) ? raw.repertorioSociocultural.length : null,
      exemplars: Array.isArray(safeAnalysis.exemplarSentences) ? safeAnalysis.exemplarSentences.length : 0,
      rawExemplars: Array.isArray(raw?.exemplarSentences) ? raw.exemplarSentences.length : null,
      roadmap: Array.isArray(safeAnalysis.roadmapTo1000) ? safeAnalysis.roadmapTo1000.length : 0,
      rawRoadmap: Array.isArray(raw?.roadmapTo1000) ? raw.roadmapTo1000.length : null,
      keys: Object.keys(safeAnalysis || {}).sort(),
      samples: {
        strengths: Array.isArray(safeAnalysis.strengths) ? safeAnalysis.strengths.slice(0, 2) : null,
        rawStrengths: Array.isArray(raw?.strengths) ? raw.strengths.slice(0, 2) : null,
        repertorio: Array.isArray(safeAnalysis.repertorioSociocultural) ? safeAnalysis.repertorioSociocultural.slice(0, 2) : null,
        rawRepertorio: Array.isArray(raw?.repertorioSociocultural) ? raw.repertorioSociocultural.slice(0, 2) : null,
        exemplars: Array.isArray(safeAnalysis.exemplarSentences) ? safeAnalysis.exemplarSentences.slice(0, 2) : null,
        rawExemplars: Array.isArray(raw?.exemplarSentences) ? raw.exemplarSentences.slice(0, 2) : null,
        roadmap: Array.isArray(safeAnalysis.roadmapTo1000) ? safeAnalysis.roadmapTo1000.slice(0, 2) : null,
        rawRoadmap: Array.isArray(raw?.roadmapTo1000) ? raw.roadmapTo1000.slice(0, 2) : null,
        penalties: Array.isArray(safeAnalysis.penaltyTable) ? safeAnalysis.penaltyTable.slice(0, 2) : null,
        rawPenalties: Array.isArray(raw?.penaltyTable) ? raw.penaltyTable.slice(0, 2) : null
      }
    };

    console.info('[EssayResultPage] Analysis snapshot', {
      essayId: essay.id,
      meta,
      rawKeys: raw ? Object.keys(raw).sort() : null,
      hasPenaltyTable: !!safeAnalysis.penaltyTable,
      hasRawPenaltyTable: !!raw?.penaltyTable
    });
    if (safeAnalysis?.scores) {
      console.info('[EssayResultPage] scores (normalized)', safeAnalysis.scores);
    }
    if (raw?.scores) {
      console.info('[EssayResultPage] scores (raw)', raw.scores);
    }
  }, [analysis, essay?.id]);

  const strengthsMeta = useMemo(() => {
    const primary = Array.isArray(analysis.strengths) ? analysis.strengths : [];
    const fallback = Array.isArray(rawAnalysis.strengths) ? rawAnalysis.strengths : [];
    const source = primary.length ? 'analysis.strengths' : (fallback.length ? 'analysis.raw.strengths' : null);
    const base = source === 'analysis.strengths' ? primary : (source ? fallback : []);
    const items = base
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          const candidate = item.text || item.value || item.label || '';
          return typeof candidate === 'string' ? candidate.trim() : '';
        }
        return '';
      })
      .filter(Boolean)
      .map((text, idx) => ({ id: `strength-${idx}`, text }));
    return { items, source, rawCount: base.length };
  }, [analysis, rawAnalysis]);

  const repertorioMeta = useMemo(() => {
    const primary = Array.isArray(analysis.repertorioSociocultural) ? analysis.repertorioSociocultural : [];
    const fallback = Array.isArray(rawAnalysis.repertorioSociocultural) ? rawAnalysis.repertorioSociocultural : [];
    const source = primary.length ? 'analysis.repertorioSociocultural' : (fallback.length ? 'analysis.raw.repertorioSociocultural' : null);
    const base = source === 'analysis.repertorioSociocultural' ? primary : (source ? fallback : []);
    const items = base
      .map((item, idx) => ({
        id: `rep-${idx}`,
        reference: typeof item?.reference === 'string' ? item.reference.trim() : '',
        application: typeof item?.application === 'string' ? item.application.trim() : ''
      }))
      .filter((item) => item.reference && item.application);
    return { items, source, rawCount: base.length };
  }, [analysis, rawAnalysis]);

  const exemplarMeta = useMemo(() => {
    const primary = Array.isArray(analysis.exemplarSentences) ? analysis.exemplarSentences : [];
    const fallback = Array.isArray(rawAnalysis.exemplarSentences) ? rawAnalysis.exemplarSentences : [];
    const source = primary.length ? 'analysis.exemplarSentences' : (fallback.length ? 'analysis.raw.exemplarSentences' : null);
    const base = source === 'analysis.exemplarSentences' ? primary : (source ? fallback : []);
    const items = base
      .map((item, idx) => ({
        id: `exemplar-${idx}`,
        excerpt: typeof item?.excerpt === 'string' ? item.excerpt.trim() : '',
        why: typeof item?.whyItWorks === 'string' ? item.whyItWorks.trim() : ''
      }))
      .filter((item) => item.excerpt && item.why);
    return { items, source, rawCount: base.length };
  }, [analysis, rawAnalysis]);

  const roadmapMeta = useMemo(() => {
    const primary = Array.isArray(analysis.roadmapTo1000) ? analysis.roadmapTo1000 : [];
    const fallback = Array.isArray(rawAnalysis.roadmapTo1000) ? rawAnalysis.roadmapTo1000 : [];
    const source = primary.length ? 'analysis.roadmapTo1000' : (fallback.length ? 'analysis.raw.roadmapTo1000' : null);
    const base = source === 'analysis.roadmapTo1000' ? primary : (source ? fallback : []);
    const items = base
      .map((item, idx) => {
        if (typeof item === 'string') {
          const text = item.trim();
          return text ? { id: `roadmap-${idx}`, text } : null;
        }
        if (item && typeof item === 'object') {
          const candidate = item.text || item.step || item.tip || '';
          if (typeof candidate === 'string' && candidate.trim()) {
            return { id: `roadmap-${idx}`, text: candidate.trim() };
          }
        }
        return null;
      })
      .filter(Boolean);
    return { items, source, rawCount: base.length };
  }, [analysis, rawAnalysis]);

  const strengths = strengthsMeta.items;
  const repertorio = repertorioMeta.items;
  const exemplarSentences = exemplarMeta.items;
  const roadmap = roadmapMeta.items;
  const paragraphFeedbackMeta = useMemo(() => {
    const primary = Array.isArray(analysis.paragraphFeedback) ? analysis.paragraphFeedback : [];
    const fallback = Array.isArray(rawAnalysis.paragraphFeedback) ? rawAnalysis.paragraphFeedback : [];
    const source = primary.length ? 'analysis.paragraphFeedback' : (fallback.length ? 'analysis.raw.paragraphFeedback' : null);
    const base = source === 'analysis.paragraphFeedback' ? primary : (source ? fallback : []);
    const items = base
      .map((item, idx) => ({
        id: `pf-${idx}`,
        paragraph: Number.isFinite(item?.paragraph) ? item.paragraph : idx + 1,
        summary: typeof item?.summary === 'string' ? item.summary.trim() : '',
        strength: typeof item?.strength === 'string' ? item.strength.trim() : '',
        improvement: typeof item?.improvement === 'string' ? item.improvement.trim() : '',
        snippet: typeof item?.snippet === 'string' ? item.snippet.trim() : ''
      }))
      .filter((item) => item.summary || item.improvement)
      .sort((a, b) => a.paragraph - b.paragraph);
    return { items, source, rawCount: base.length };
  }, [analysis, rawAnalysis]);
  const paragraphFeedback = paragraphFeedbackMeta.items;

  const scoreSummary = useMemo(() => {
    const comps = analysis.scores || {};
    const sum = Object.values(comps).reduce((acc, comp) => acc + (Number(comp?.score) || 0), 0);
    const totalPenalty = Number.isFinite(analysis.totalPenalty) ? analysis.totalPenalty : 0;
    return { sumCompetencies: sum, totalPenalty, mismatch: !!analysis.penaltyMismatch };
  }, [analysis]);

  const penaltyData = useMemo(() => {
    const rawPenalties = Array.isArray(rawAnalysis.penaltyTable) ? rawAnalysis.penaltyTable : [];
    const fromModel = Array.isArray(analysis.penaltyTable) && analysis.penaltyTable.length
      ? analysis.penaltyTable
      : rawPenalties;
    const hasModelPenalties = fromModel.length > 0;

    const computed = (() => {
      const errs = Array.isArray(inlineErrors) ? inlineErrors : [];
      const seenByType = {};
      const penalizations = [];
      errs.forEach((err) => {
        const t = err.type || 'other';
        seenByType[t] = (seenByType[t] || 0) + 1;
        // First occurrence is tolerated; only penalize repeats
        if (seenByType[t] <= 1) return;
        penalizations.push({
          type: t,
          snippet: typeof err.originalText === 'string' && err.originalText.trim() ? err.originalText.trim() : '',
          points: 40,
          explanation: err.explanation || err.message || err.correction || ''
        });
      });
      return penalizations;
    })();

    const penalties = hasModelPenalties
      ? fromModel.map((p, idx) => ({
          id: p.id || `penalty-${idx}`,
          type: p.type || 'penalty',
          snippet: typeof p.snippet === 'string' ? p.snippet : '',
          points: Number.isFinite(p.points) ? p.points : 40,
          explanation: p.description || p.explanation || ''
        }))
      : computed.map((p, idx) => ({ id: `computed-${idx}`, ...p }));

    const totalPenalty = penalties.reduce((sum, p) => sum + (p.points || 0), 0);
    const logPayload = {
      essayId: essay?.id || null,
      source: hasModelPenalties ? 'model' : 'computed',
      hasModelPenalties,
      fromModelCount: fromModel.length,
      rawPenaltiesCount: rawPenalties.length,
      computedCount: computed.length,
      inlineErrorsCount: Array.isArray(inlineErrors) ? inlineErrors.length : 0,
      totalPenalty,
      sample: penalties.slice(0, 2)
    };
    if (hasModelPenalties || rawPenalties.length) {
      console.info('[EssayResultPage] penaltyData (model/raw)', logPayload);
    } else {
      console.warn('[EssayResultPage] penaltyData missing in analysis/raw; using computed fallback', logPayload);
    }
    return { penalizations: penalties, totalPenalty, source: hasModelPenalties ? 'model' : 'computed' };
  }, [analysis.penaltyTable, rawAnalysis.penaltyTable, inlineErrors, essay?.id]);

  const hasGuidedSections = strengths.length > 0 || repertorio.length > 0 || exemplarSentences.length > 0 || roadmap.length > 0;
  console.log(strengths.length)
  console.log(repertorio.length)
  console.log(exemplarSentences.length)
  console.log(roadmap.length)

  useEffect(() => {
    if (!essay?.id) return;
    if (strengthsMeta.source === 'analysis.raw.strengths' && strengths.length) {
      console.warn('[EssayResultPage] Using raw strengths fallback', {
        essayId: essay.id,
        rawCount: strengthsMeta.rawCount,
        normalizedCount: strengths.length
      });
    } else if (!strengths.length) {
      console.info('[EssayResultPage] Nenhum strength disponível na análise carregada', { essayId: essay.id });
    }
  }, [essay?.id, strengths.length, strengthsMeta.rawCount, strengthsMeta.source]);

  useEffect(() => {
    if (!essay?.id) return;
    if (repertorioMeta.source === 'analysis.raw.repertorioSociocultural' && repertorio.length) {
      console.warn('[EssayResultPage] Using raw repertório fallback', {
        essayId: essay.id,
        rawCount: repertorioMeta.rawCount,
        normalizedCount: repertorio.length
      });
    } else if (!repertorio.length) {
      const detail = Array.isArray(rawAnalysis?.repertorioSociocultural)
        ? rawAnalysis.repertorioSociocultural.slice(0, 3)
        : null;
      console.info('[EssayResultPage] Nenhum repertório sociocultural encontrado na análise', {
        essayId: essay.id,
        rawCount: repertorioMeta.rawCount,
        rawSample: detail
      });
    }
  }, [essay?.id, repertorio.length, repertorioMeta.rawCount, repertorioMeta.source, rawAnalysis?.repertorioSociocultural]);

  useEffect(() => {
    if (!essay?.id) return;
    if (exemplarMeta.source === 'analysis.raw.exemplarSentences' && exemplarSentences.length) {
      console.warn('[EssayResultPage] Using raw exemplar sentences fallback', {
        essayId: essay.id,
        rawCount: exemplarMeta.rawCount,
        normalizedCount: exemplarSentences.length
      });
    } else if (!exemplarSentences.length) {
      const detail = Array.isArray(rawAnalysis?.exemplarSentences)
        ? rawAnalysis.exemplarSentences.slice(0, 3)
        : null;
      console.info('[EssayResultPage] Nenhum trecho exemplar identificado na análise', {
        essayId: essay.id,
        rawCount: exemplarMeta.rawCount,
        rawSample: detail
      });
    }
  }, [essay?.id, exemplarSentences.length, exemplarMeta.rawCount, exemplarMeta.source, rawAnalysis?.exemplarSentences]);

  useEffect(() => {
    if (!essay?.id) return;
    if (roadmapMeta.source === 'analysis.raw.roadmapTo1000' && roadmap.length) {
      console.warn('[EssayResultPage] Using raw roadmap fallback', {
        essayId: essay.id,
        rawCount: roadmapMeta.rawCount,
        normalizedCount: roadmap.length
      });
    } else if (!roadmap.length) {
      console.info('[EssayResultPage] Nenhum roadmap para nota 1000 presente na análise', { essayId: essay.id });
    }
  }, [essay?.id, roadmap.length, roadmapMeta.rawCount, roadmapMeta.source]);
  const examType = essay?.examType || analysis?.examType || 'enem';
  const maxScore = analysis?.maxScore || essayService.getMaxScoreForExamType(examType);

  const goBack = () => navigate('/enem-training');

  const handleJumpToOffsets = useCallback((start, end) => {
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      setActiveRange({ start, end });
      setTimeout(() => setActiveRange(null), 600);
    }
  }, []);

  // Get exam type display info
  const getExamTypeInfo = () => {
    const examInfo = {
      enem: { name: 'ENEM', fullName: 'Exame Nacional do Ensino Médio' },
      fuvest: { name: 'FUVEST', fullName: 'Vestibular USP' },
      unicamp: { name: 'UNICAMP', fullName: 'Vestibular UNICAMP' },
      uerj: { name: 'UERJ', fullName: 'Vestibular Estadual do Rio de Janeiro' }
    };
    return examInfo[examType] || examInfo.enem;
  };

  const examInfo = getExamTypeInfo();

  if (loading) {
    return (
      <div className="enem-training-page">
        <div className="ai-analysis-panel">
          <p>Carregando análise…</p>
        </div>
      </div>
    );
  }

  if (error || !essay) {
    return (
      <div className="enem-training-page">
        <div className="ai-analysis-panel">
          <p className="error-message">{error || 'Redação não encontrada.'}</p>
          <button className="btn btn-secondary" onClick={goBack}>Voltar</button>
        </div>
      </div>
    );
  }

  const hasText =
    essay?.type !== 'image' &&
    typeof essay?.content === 'string' &&
    essay.content.trim().length > 0;

  const showTransparencySection = false; // Temporarily hide score transparency table

  // Render competencies/criteria based on exam type
  const renderScores = () => {
    if (!analysis.scores) return null;

    const scores = analysis.scores;
    const scoreEntries = Object.entries(scores);

    return (
      <div className="competency-scores">
        <h4>
          {examType === 'enem' && 'Desempenho por Competência'}
          {examType === 'fuvest' && 'Desempenho por Critério'}
          {examType === 'unicamp' && 'Desempenho por Critério'}
          {examType === 'uerj' && 'Desempenho por Critério'}
        </h4>
        
        {scoreEntries.map(([key, scoreData]) => {
          const score = scoreData.score || 0;
          const compMaxScore = scoreData.maxScore || 200;
          const label = scoreData.label || key;
          const pct = Math.max(0, Math.min(100, (score / compMaxScore) * 100));
          const colorClass =
            pct >= 80 ? 'excellent' : pct >= 60 ? 'good' : pct >= 40 ? 'average' : 'poor';
          
          return (
            <div key={key} className="competency-item">
              <div className="competency-header">
                <span className="competency-name">{label}</span>
                <span className={`competency-score ${colorClass}`}>
                  {score}/{compMaxScore}
                </span>
              </div>
              <div className="competency-bar">
                <div className={`competency-fill ${colorClass}`} style={{ width: `${pct}%` }} />
              </div>
              {scoreData.justification && (
                <p className="competency-justification">{scoreData.justification}</p>
              )}
            </div>
          );
        })}
        
        {typeof analysis.totalScore === 'number' && (
          <div className="total-score">
            <strong>Nota Total: {analysis.totalScore}/{maxScore}</strong>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="enem-training-page">
      <header className="page-header">
        <h1>Correção da Redação</h1>
        <p>{essay.title}</p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
          <span style={{ 
            background: '#e7f3ff', 
            padding: '4px 12px', 
            borderRadius: 4, 
            fontSize: '0.9em',
            color: '#0c5aa6',
            fontWeight: 600
          }}>
            {examInfo.name}
          </span>
          <button className="progress-toggle" onClick={goBack}>Voltar</button>
        </div>
      </header>

      {/* === RESULTS FIRST === */}
      <section className="ai-analysis-panel" style={{ marginTop: 10 }}>
        <div className="analysis-results">
          <div className="analysis-header">
            <h4>Resultados da Análise - {examInfo.fullName}</h4>
          </div>

          {analysis.model && (
            <div className="analysis-meta" style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 16,
              fontSize: '0.9em',
              color: '#495057'
            }}>
              <span style={{ background: '#f1f3f5', padding: '4px 10px', borderRadius: 6 }}>
                <strong>Modelo:</strong> {analysis.model}
              </span>
            </div>
          )}

          {/* Genre for UNICAMP */}
          {examType === 'unicamp' && analysis.genre && (
            <div style={{ 
              background: '#f8f9fa', 
              padding: 12, 
              borderRadius: 8, 
              marginBottom: 16,
              border: '1px solid #dee2e6'
            }}>
              <strong>Gênero Textual Identificado:</strong> {analysis.genre}
            </div>
          )}

          {/* Competency/Criteria scores */}
          {renderScores()}

          {showTransparencySection && (
          <div style={{ marginTop: 16, background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
            <h4 style={{ marginTop: 0 }}>Transparência da pontuação</h4>
            <p style={{ marginBottom: 8 }}>
              Nota máxima: <strong>{maxScore}</strong> · Nota final: <strong>{analysis.totalScore}</strong>
            </p>
            <p style={{ marginBottom: 8, color: '#334155' }}>
              Soma das competências: <strong>{scoreSummary.sumCompetencies}</strong> · Penalizações: <strong>{scoreSummary.totalPenalty}</strong>
            </p>
            {scoreSummary.mismatch && (
              <div style={{ marginBottom: 8, background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', padding: 8, borderRadius: 6 }}>
                ⚠️ Penalizações ou notas ajustadas para manter consistência (model output divergente).
              </div>
            )}
            <p style={{ marginBottom: 12, color: '#495057' }}>
              As penalizações abaixo seguem a regra “tolerar o primeiro erro, -40 pontos por repetição do mesmo tipo”.
              Fonte: {penaltyData.source === 'model' ? 'fornecida pelo modelo (penaltyTable)' : 'estimada a partir das correções inline'}.
            </p>
            {penaltyData.penalizations.length > 0 ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {penaltyData.penalizations.map((p, idx) => (
                  <div key={idx} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ color: '#b91c1c' }}>-{p.points} pts</strong>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{p.type}</span>
                    </div>
                    {p.snippet && <blockquote style={{ margin: '6px 0', paddingLeft: 10, borderLeft: '3px solid #e5e7eb', color: '#374151' }}>“{p.snippet}”</blockquote>}
                    {p.explanation && <div style={{ fontSize: 13, color: '#4b5563' }}>{p.explanation}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 8, padding: 10, color: '#2e7d32' }}>
                Nenhuma penalização por repetição de erro foi aplicada.
              </div>
            )}
            <p style={{ marginTop: 12, fontWeight: 600 }}>
              Dedução total estimada: {penaltyData.totalPenalty} pts · Nota final exibida: {analysis.totalScore}
            </p>
          </div>
          )}

          {/* Inline errors list with direct jump by offsets */}
          {Array.isArray(inlineErrors) && inlineErrors.length > 0 && (
            <div className="inline-errors-section">
              <div className="section-header">
                <h4>Correções Inline ({inlineErrors.length})</h4>
              </div>
              <div className="inline-errors-list">
                {inlineErrors.map((err, idx) => (
                  <div key={idx} className={`inline-error-item error-type-${err.type || 'other'}`}>
                    <div className="error-header">
                      <span className="error-type">{err.type || 'Erro'}</span>
                      <button
                        className="toggle-analysis"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => handleJumpToOffsets(err.start, err.end)}
                        disabled={!hasText}
                        title={hasText ? 'Ir ao trecho no texto' : 'Não disponível para imagem'}
                      >
                        Ir ao trecho
                      </button>
                    </div>
                    <div className="error-content">
                      <div className="error-original">
                        <strong>Original:</strong> "{err.originalText}"
                      </div>
                      <div className="error-correction">
                        <strong>Sugestão:</strong> "{err.correction}"
                      </div>
                      {err.explanation && (
                        <div className="error-explanation">{err.explanation}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Problems / Suggestions */}
          {(Array.isArray(analysis.problems) && analysis.problems.length > 0) && (
            <div className="problems-section">
              <h4>Principais Problemas</h4>
              <ul className="problems-list">
                {analysis.problems.map((p, i) => (
                  <li key={i} className="problem-item">{p}</li>
                ))}
              </ul>
            </div>
          )}
          {(Array.isArray(analysis.suggestions) && analysis.suggestions.length > 0) && (
            <div className="suggestions-section">
              <h4>Sugestões de Melhoria</h4>
              <ul className="suggestions-list">
                {analysis.suggestions.map((s, i) => (
                  <li key={i} className="suggestion-item">{s}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Feedback Summary */}
          {feedbackSummary && (
            <div className="suggestions-section">
              <h4>Resumo do Feedback</h4>
              <p style={{ lineHeight: 1.6, color: '#495057' }}>{feedbackSummary}</p>
            </div>
          )}

          {paragraphFeedback.length > 0 && (
            <div className="paragraph-feedback">
              <h4>Feedback por parágrafo</h4>
              <div className="paragraph-grid">
                {paragraphFeedback.map((item) => (
                  <article key={item.id} className="paragraph-card">
                    <div className="paragraph-meta">Parágrafo {item.paragraph}</div>
                    <p className="paragraph-summary">{item.summary}</p>
                    {item.strength && (
                      <p className="paragraph-strength"><strong>Ponto forte:</strong> {item.strength}</p>
                    )}
                    {item.improvement && (
                      <p className="paragraph-improvement"><strong>Melhorar:</strong> {item.improvement}</p>
                    )}
                    {item.snippet && <blockquote>“{item.snippet}”</blockquote>}
                  </article>
                ))}
              </div>
            </div>
          )}

          {/* Guided practice sections */}
          {hasGuidedSections && (
            <div className="guided-sections">
              {strengths.length > 0 && (
                <div className="feedback-section strengths-section">
                  <h4>Principais Pontos Fortes</h4>
                  <ul className="strengths-list">
                    {strengths.map((item) => (
                      <li key={item.id}>{item.text}</li>
                    ))}
                  </ul>
                </div>
              )}

              {exemplarSentences.length > 0 && (
                <div className="feedback-section exemplar-section">
                  <h4>Trechos Exemplares do Texto</h4>
                  <div className="exemplar-grid">
                    {exemplarSentences.map((item) => (
                      <article key={item.id} className="exemplar-card">
                        <blockquote>“{item.excerpt}”</blockquote>
                        <p className="exemplar-reason">{item.why}</p>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {repertorio.length > 0 && (
                <div className="feedback-section repertorio-section">
                  <h4>Repertórios Socioculturais Sugeridos</h4>
                  <div className="repertorio-grid">
                    {repertorio.map((item) => (
                      <div key={item.id} className="repertorio-card">
                        <strong>{item.reference}</strong>
                        <p>{item.application}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {roadmap.length > 0 && (
                <div className="feedback-section roadmap-section">
                  <h4>Plano de Ação Rumo à Nota 1000</h4>
                  <ol className="roadmap-list">
                    {roadmap.map((item, idx) => (
                      <li key={item.id}>
                        <span className="step-index">Etapa {idx + 1}</span>
                        <span className="step-text">{item.text}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
          
          {/* Corrected Version (if available) */}
          {analysis.correctedVersion && (
            <div className="corrected-version">
              <h4>Versão Corrigida</h4>
              <div className="corrected-text">{analysis.correctedVersion}</div>
            </div>
          )}
        </div>
      </section>

      {/* === "SEU TEXTO" ONLY WHEN THERE IS TEXT === */}
      {hasText && (
        <section className="essay-form-section">
          <h2>Seu Texto</h2>
          <div className="form-group">
            <label>Conteúdo</label>
            {!!inlineErrors.length && (
              <label className="markup-toggle" style={{ marginLeft: 8 }}>
                <input
                  type="checkbox"
                  checked={showMarkups}
                  onChange={(e) => setShowMarkups(e.target.checked)}
                />
                Mostrar marcações ({inlineErrors.length})
              </label>
            )}
            <InlineMarkupEditor
              content={essay.content || ''}
              onChange={() => {}}
              inlineErrors={showMarkups ? inlineErrors : []}
              showMarkups={showMarkups}
              activeRange={activeRange}
              placeholder="Sem conteúdo"
              readOnly
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default EssayResultPage;
